import {
  NON_AUTH_ENDPOINT,
  NON_AUTH_PAGE,
} from '@jobseeker/common-core/constants';
import axios from 'axios';
//import {AxiosError} from 'axios';
import qs from 'qs';
import {
  clearAuth,
  isSnackbarSuppressedPath,
  logErrorToast,
} from '@jobseeker/common-core/utils';
import {openUrl} from '@jobseeker/common-core/utils/common';
import {enqueueSnackbar} from 'notistack';

const TOAST_THROTTLE_MS = 10000;
const lastToastTime: Record<string, number> = {};

let isNavigatingAway = false;
if (typeof window !== 'undefined') {
  const markNavigating = () => {
    isNavigatingAway = true;
  };
  window.addEventListener('beforeunload', markNavigating);
  window.addEventListener('pagehide', markNavigating);
  window.addEventListener('pageshow', () => {
    isNavigatingAway = false;
  });
}

const throttledSnackbar = (
  key: string,
  message: string,
  variant: 'error' | 'success' | 'warning' | 'info',
  error?: any
) => {
  const now = Date.now();
  if (now - (lastToastTime[key] || 0) < TOAST_THROTTLE_MS) return;
  lastToastTime[key] = now;

  // On these pages, suppress the visible snackbar but still audit-log the error.
  if (variant === 'error' && isSnackbarSuppressedPath()) {
    logErrorToast(key, message, error);
    return;
  }

  enqueueSnackbar(message, {
    variant,
    ...(variant === 'error'
      ? {onEntered: () => logErrorToast(key, message, error)}
      : {}),
  });
};

const reloadLocation = () => {
  let href = '/';
  const publicPath = ['/c/', '/k/', '/j/'];
  const pathName = window.location?.pathname || '/';
  for (const path of publicPath) {
    if (pathName.includes(path)) {
      href = window.location?.href;
    }
  }

  openUrl(href, '_self');
};

const handleRefresh = async (config: object) => {
  const AUTH_API_PATH: string =
    `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_AUTH}/refresh` ||
    '';

  const response = await axios.get(`${AUTH_API_PATH}`, config);
  return response?.['data']?.token;
};

const handleLogout = async (config: object) => {
  await axios.post(
    `${process.env.NEXT_PUBLIC_API_GATEWAY_GLADIATOR}/${process.env.NEXT_PUBLIC_PREFIX_USER_SERVICE}/logout`,
    qs.stringify({}),
    config
  );
};

export const csrHandler = async (error: any /** AxiosError */) => {
  if (
    error?.response?.status === 401 &&
    !NON_AUTH_ENDPOINT.includes(error?.config?.url || '')
  ) {
    const pathSegment = window.location?.pathname?.split('/')[1];
    if (
      NON_AUTH_PAGE.includes(pathSegment) ||
      pathSegment?.includes('careers') ||
      pathSegment?.includes('jobs-in')
    ) {
      return;
    }
    openUrl('/', '_self');
  }

  if (
    [406].includes(error?.response?.status || 0) &&
    !NON_AUTH_ENDPOINT.includes(error?.config?.url || '')
  ) {
    const config: {[key: string]: boolean | object | string} = {
      withCredentials: true,
    };

    try {
      await handleRefresh(config);
    } catch (error: any) {
      if (error?.response?.status === 406) {
        await handleLogout({
          withCredentials: true,
        });
        clearAuth();
        reloadLocation();
      }
    }

    const data = await axios(error.config);
    if (data?.status === 200) {
      return data;
    }
    return axios(error.config);
  }

  if (
    error?.response?.status === 418 &&
    !NON_AUTH_ENDPOINT.includes(error?.config?.url || '')
  ) {
    // status code 418 is fixed for intensional logout cases
    await handleLogout({
      withCredentials: true,
    });
    clearAuth();
    reloadLocation();
  } else {
    const suppressToast =
      error?.config?.suppressErrorToast === true || isNavigatingAway;

    if (!navigator.onLine) {
      // Real offline status
      const message = 'Please check your internet connection and try again.';
      const response = {
        data: {
          error: {
            message,
            statusCode: 0,
          },
        },
      };
      error['response'] = response;

      if (!suppressToast) throttledSnackbar('offline', message, 'error', error);
      return Promise.reject(error);
    } else if (error?.code === 'ECONNABORTED') {
      const message =
        'Request timed out. Please check your connection and try again.';
      const response = {
        data: {
          error: {
            message,
            statusCode: 0,
          },
        },
      };
      error['response'] = response;

      if (!suppressToast)
        throttledSnackbar('network-timeout', message, 'error', error);
      return Promise.reject(error);
    } else if (error?.message === 'Network Error') {
      const message = 'Something went wrong. Please try again later.';
      const response = {
        data: {
          error: {
            message,
            statusCode: 0,
          },
        },
      };
      error['response'] = response;

      if (!suppressToast)
        throttledSnackbar('network-error', message, 'error', error);
      return Promise.reject(error);
    } else if ((error?.response?.status || 0) >= 500) {
      const message = 'Something went wrong, please try again after some time.';
      const response = {
        data: {
          error: {
            message,
            statusCode: 0,
          },
        },
      };
      error['response'] = response;
      if (!suppressToast)
        throttledSnackbar('server-error', message, 'error', error);
    }

    return Promise.reject(error);
  }
};

export const ssrHandler = async (error: any /** AxiosError */) => {
  if (
    error?.response?.status === 401 &&
    !NON_AUTH_ENDPOINT.includes(error?.config?.url || '')
  ) {
    return {
      type: 'logout',
    };
  }
  if (
    [406].includes(error?.response?.status || 0) &&
    !NON_AUTH_ENDPOINT.includes(error?.config?.url || '')
  ) {
    try {
      const headers = {
        cookie: error.config?.headers?.cookie,
      };

      const token = await handleRefresh({headers});
      return token ? {type: 'refresh', token} : false;
    } catch (error: any) {
      if (error?.response?.status === 406) {
        const headers = {
          cookie: error.config?.headers?.cookie,
        };

        try {
          await handleLogout({headers});
          return {
            type: 'logout',
          };
        } catch (err) {
          return {
            type: 'logout',
          };
        }
      }
    }

    return axios(error.config);
  } else if (error?.response?.status === 418) {
    // status code 418 is fixed for intensional logout cases
    const headers = {
      cookie: error.config?.headers?.cookie,
    };

    try {
      await handleLogout({headers});
      return {
        type: 'logout',
      };
    } catch (err) {
      return {
        type: 'logout',
      };
    }
  } else {
    return Promise.reject(error);
  }
};
